import { Fragment } from 'react/jsx-runtime';

import { Tooltip, makeStyles } from '@material-ui/core';

import classNames from 'classnames';
import isEqual from 'lodash/isEqual';

import { Text } from '@backstage/ui';

import {
  ActivityItem,
  DeltaText,
} from '@spotify/plugin-confidence-activity-react';

import { Variant } from './types';

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: theme.spacing(0.5),
    display: 'grid',
    gridTemplateColumns: '1fr minmax(100px, 2fr)',
    gap: theme.spacing(0.25),
    '& > *': {
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
    },
  },
  addedText: {
    color: 'var(--bui-fg-success)',
  },
  removedText: {
    color: 'var(--bui-fg-danger)',
    textDecoration: 'line-through',
  },
  modifiedText: {
    color: 'var(--bui-fg-primary)',
  },
}));

// calculate "added" | "removed" | "modified" | "unchanged" for each property
export const getPropertyChanges = (
  properties: Record<string, any>,
  oldProperties?: Record<string, any>,
  newProperties?: Record<string, any>,
  oldVariant?: Variant,
  newVariant?: Variant,
) => {
  return Object.entries(properties).map(([key]) => {
    let change = 'unchanged';
    const oldValue = oldProperties?.[key];
    const newValue = newProperties?.[key];

    if (oldValue === undefined) {
      change = 'added';
    } else if (newValue === undefined) {
      change = 'removed';
    } else if (!isEqual(oldValue, newValue)) {
      change = 'modified';
    }

    // if we do not have any old variant, we consider it "unchanged" as that is how
    // we wanna represent the new variant.
    if (!oldVariant && newVariant) {
      change = 'unchanged';
    }

    // if we do not have any new variant, we consider it "unchanged" as that is how
    // we wanna represent the deleted variant.
    if (oldVariant && !newVariant) {
      change = 'unchanged';
    }

    return { key, oldValue, newValue, change };
  });
};

export interface PropertySectionProps {
  oldVariant?: Variant;
  newVariant?: Variant;
}

export const PropertiesSection = ({
  oldVariant,
  newVariant,
}: {
  oldVariant?: Variant;
  newVariant?: Variant;
}) => {
  const classes = useStyles();

  const oldProperties = oldVariant?.value;
  const newProperties = newVariant?.value;

  const properties = { ...oldProperties, ...newProperties };

  const changes = getPropertyChanges(
    properties,
    oldProperties,
    newProperties,
    oldVariant,
    newVariant,
  );

  return (
    <ActivityItem.Section label="Properties">
      <div className={classes.root}>
        {changes.map(({ key, oldValue, newValue, change }) => {
          const newText =
            typeof newValue === 'string' ? newValue : JSON.stringify(newValue);
          const oldText =
            typeof oldValue === 'string' ? oldValue : JSON.stringify(oldValue);

          return (
            <Fragment key={key}>
              <Tooltip title={key} arrow enterDelay={700}>
                <Text
                  variant="body-medium"
                  className={classNames(
                    change === 'added' && classes.addedText,
                    change === 'removed' && classes.removedText,
                    change === 'modified' && classes.modifiedText,
                  )}
                >
                  {key}
                </Text>
              </Tooltip>

              <Tooltip title={newText ?? oldText ?? ''} arrow enterDelay={700}>
                {change !== 'unchanged' ? (
                  <DeltaText
                    oldText={oldText ?? ''}
                    newText={newText ?? ''}
                    disableDiff={
                      // if the value is not a string, we don't want to show the
                      // char-by-char diff
                      typeof oldValue !== 'string' &&
                      typeof newValue !== 'string'
                    }
                  />
                ) : (
                  <Text variant="body-medium">{newText ?? oldText}</Text>
                )}
              </Tooltip>
            </Fragment>
          );
        })}
      </div>
    </ActivityItem.Section>
  );
};
